import { IconButton } from '@creditornot/cb-button';
import { Copy, MoreHorizontal, Pencil, Trashbin } from '@creditornot/cb-icons';
import { MenuActionItem, MenuItemGroup } from '@creditornot/cb-menu';
import { Popover } from '@creditornot/cb-popover';
import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import styled from 'styled-components';

import { Translate } from 'common/components/icons';
import { useListingManagerRoutes } from 'modules/shared-menu/hooks/useListingManagerRoutes';
import { SharedMenu } from 'modules/shared-menu/types';

import { DeleteSharedMenuModal } from './DeleteSharedMenuModal';
import { DuplicateSharedMenuModal } from './DuplicateSharedMenuModal';
import { EmptyToDeleteSharedMenuModal } from './EmptyToDeleteSharedMenuModal';
import { RenameSharedMenuModal } from '../../SharedMenuPage/components/RenameSharedMenuModal';

type SharedMenuActionsProps = {
  sharedMenu: SharedMenu;
};

const StyledPopover = styled(Popover).attrs({
  placement: 'bottom-start',
  renderArrow: false,
})`
  display: flex;
  align-items: center;
`;

const ToggleButton = styled(IconButton).attrs({ variant: 'blue', size: 'tiny' })``;

const PopoverContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 200px;
  padding: 8px;
`;

const PopoverItem = styled(MenuActionItem)`
  margin-block-start: 8px;
`;

export const SharedMenuActions = ({ sharedMenu }: SharedMenuActionsProps) => {
  const history = useHistory();
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEmptyToDeleteModalOpen, setEmptyToDeleteModalOpen] = useState(false);
  const [show, setShow] = useState(false);
  const { menuEditorTranslationsPage } = useListingManagerRoutes();

  const toggle = () => setShow(oldState => !oldState);
  const close = () => setShow(false);

  const isMenuDeletable = useMemo(() => !sharedMenu.linked_venue_ids.length, [sharedMenu]);

  return (
    <>
      <StyledPopover
        content={
          <PopoverContent>
            <MenuItemGroup title={<FormattedMessage id="shared-menu.shared-menu-view.menu-actions.title" />}>
              <PopoverItem variant="black" icon={<Copy />} onClick={() => setIsDuplicateModalOpen(true)}>
                <FormattedMessage id="shared-menu.shared-menu-view.menu-actions.duplicate" />
              </PopoverItem>
              <PopoverItem variant="black" icon={<Pencil />} onClick={() => setIsRenameModalOpen(true)}>
                <FormattedMessage id="shared-menu.shared-menu-view.menu-actions.rename" />
              </PopoverItem>
              <PopoverItem
                variant="black"
                icon={<Translate />}
                onClick={() => history.push(menuEditorTranslationsPage())}
              >
                <FormattedMessage id="shared-menu.shared-menu-view.menu-actions.translate" />
              </PopoverItem>
              <PopoverItem
                variant="red"
                icon={<Trashbin />}
                onClick={() => (isMenuDeletable ? setIsDeleteModalOpen(true) : setEmptyToDeleteModalOpen(true))}
              >
                <FormattedMessage id="shared-menu.shared-menu-view.menu-actions.delete" />
              </PopoverItem>
            </MenuItemGroup>
          </PopoverContent>
        }
        onClickOutside={close}
        onClickInside={close}
        show={show}
      >
        <ToggleButton onClick={toggle}>
          <MoreHorizontal />
        </ToggleButton>
      </StyledPopover>

      <DuplicateSharedMenuModal
        sharedMenu={sharedMenu}
        onClose={() => setIsDuplicateModalOpen(false)}
        isOpen={isDuplicateModalOpen}
      />
      {isRenameModalOpen && (
        <RenameSharedMenuModal
          sharedMenu={sharedMenu}
          onClose={() => setIsRenameModalOpen(false)}
          isOpen={isRenameModalOpen}
        />
      )}
      <DeleteSharedMenuModal
        sharedMenu={sharedMenu}
        onClose={() => setIsDeleteModalOpen(false)}
        isOpen={isDeleteModalOpen}
      />
      <EmptyToDeleteSharedMenuModal
        onClose={() => setEmptyToDeleteModalOpen(false)}
        isOpen={isEmptyToDeleteModalOpen}
      />
    </>
  );
};
